import React, { useState, useEffect, useRef } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
// import BankGradeCard from "./BankGradeCard";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SEO from "./SEO";

gsap.registerPlugin(ScrollTrigger);

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    consent: true,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY,
          subject: "New Contact Form Submission",
          sender_name: formData.fullName,
          sender_email: formData.email,
          consent: formData.consent ? "Accepted" : "Not Accepted",
          message: formData.message,
          phone: formData.phone,
          reply_to: formData.email,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert("Message sent successfully!");
        setFormData({ fullName: "", email: "", consent: true, phone: "", message: "" });
      } else {
        alert("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-6 py-4 bg-white border border-gray-200 rounded-2xl focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all duration-300 font-medium text-text-primary placeholder:text-text-secondary/50 text-sm";

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headerRef.current, {
        y: 40, opacity: 0, duration: 1, ease: "power4.out", delay: 0.2, clearProps: "all",
      });
      gsap.from(leftRef.current, {
        x: -60, opacity: 0, duration: 1.2, ease: "power4.out", delay: 0.4, clearProps: "all",
      });
      gsap.from(rightRef.current, {
        x: 60, opacity: 0, duration: 1.2, ease: "power4.out", delay: 0.4, clearProps: "all",
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="min-h-screen bg-white text-text-primary font-sans selection:bg-primary-light selection:text-primary">
      <SEO
        title="Contact Us"
        description={`Get in touch with ${import.meta.env.VITE_COMPANY_NAME}. We are here to help you with your loan application or any queries.`}
        keywords={`contact ${import.meta.env.VITE_COMPANY_NAME}, customer support, loan help, financial assistance India`}
      />
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-gradient-to-b from-white via-blue-50/50 to-white" />
      <div className="relative z-20">
        <Navbar />
      </div>

      {/* Clean Corporate Hero */}
      <section className="w-full pt-40 pb-16 text-center px-6 relative overflow-hidden mb-8">
        <div ref={headerRef} className="relative z-10 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-white border border-blue-100 shadow-sm rounded-full mb-8">
            <div className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold tracking-widest text-text-secondary uppercase">Customer Support</span>
          </div>
          <h1 className="text-4xl md:text-7xl font-extrabold tracking-tight leading-tight mb-6 text-text-primary">
            Get In <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Touch.</span>
          </h1>
          <p className="text-text-secondary text-lg md:text-xl max-w-2xl mx-auto font-medium leading-relaxed">
            Our support team is available 24/7. Send us a message and we'll get back to you shortly.
          </p>
        </div>
      </section>

      <div className="relative z-10 max-w-[1440px] mx-auto grid md:grid-cols-12 gap-8 px-6 lg:px-12 mb-24">

        {/* LEFT SIDE – Contact Info */}
        <div ref={leftRef} className="md:col-span-12 lg:col-span-5 flex flex-col bg-white p-10 md:p-14 rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="space-y-10">
            <div className="space-y-4">
              <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">Contact Information</h2>
              <p className="text-text-secondary font-medium text-base leading-relaxed">
                Reach out to us via any of these channels.
              </p>
            </div>

            <div className="space-y-8">
              {[
                { icon: "📞", label: "Phone Support", value: import.meta.env.VITE_COMPANY_PHONE, href: `tel:${import.meta.env.VITE_COMPANY_PHONE}` },
                { icon: "✉️", label: "Email Us", value: import.meta.env.VITE_COMPANY_EMAIL, href: `mailto:${import.meta.env.VITE_COMPANY_EMAIL}` },
                { icon: "📍", label: "Our Office", value: import.meta.env.VITE_COMPANY_ADDRESS, href: undefined },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-5 p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
                  <span className="text-2xl mt-1">{item.icon}</span>
                  <div className="flex flex-col gap-1">
                    <div className="text-xs font-bold text-text-secondary uppercase tracking-widest">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} className="text-text-primary font-bold hover:text-primary transition-colors text-base leading-snug">{item.value}</a>
                    ) : (
                      <span className="text-text-primary font-bold text-base leading-snug">{item.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Google Map */}
          <div className="mt-10 rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
            <iframe
              title={import.meta.env.VITE_COMPANY_NAME}
              src={import.meta.env.VITE_COMPANY_ADDRESS_MAP_URL}
              width="100%"
              height="280"
              className="border-0 block"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>

        {/* RIGHT SIDE – Contact Form */}
        <form
          ref={rightRef}
          onSubmit={handleSubmit}
          className="md:col-span-12 lg:col-span-7 bg-white p-10 md:p-14 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col gap-8"
        >
          <div className="mb-2">
            <div className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Send a Message</div>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight mb-2">
              How can we <span className="text-primary">help you?</span>
            </h2>
            <p className="text-text-secondary text-sm font-medium">Fill in the form and our team will respond within 24 hours.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <input type="text" name="fullName" placeholder="Full Name" value={formData.fullName} onChange={handleChange} required className={inputClass} />
            <input type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleChange} required className={inputClass} />
            <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required className={inputClass} />
            <div className="md:col-span-2">
              <textarea name="message" placeholder="How can we help you?" value={formData.message} onChange={handleChange} rows={5} className={inputClass} />
            </div>
          </div>

          <div className="flex items-start gap-4 p-5 bg-blue-50/50 rounded-2xl border border-blue-100">
            <input
              type="checkbox"
              name="consent"
              id="consent"
              checked={formData.consent}
              onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
              className="mt-1 h-5 w-5 rounded border-2 border-primary text-primary focus:ring-primary cursor-pointer"
            />
            <label htmlFor="consent" className="text-sm text-text-secondary font-medium leading-relaxed cursor-pointer">
              I consent to receive communications from {import.meta.env.VITE_COMPANY_NAME} regarding my query.
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-5 bg-primary text-white font-bold text-base rounded-2xl shadow-lg hover:bg-secondary transition-all duration-300 disabled:opacity-60"
          >
            {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>

      <Footer />
    </div>
  )
};


export default ContactUs;
